import type { Quote, YieldData } from "../../services/pulseService";
import { formatPrice, formatPercent, changeColor } from "./utils";

interface Props {
  commodities: Quote[];
  currencies: Quote[];
  yields: YieldData | null;
}

const YIELD_LABELS: Record<string, string> = {
  treasury_2y: "2Y Treasury",
  treasury_5y: "5Y Treasury",
  treasury_10y: "10Y Treasury",
  treasury_30y: "30Y Treasury",
  spread_10y_2y: "10Y-2Y Spread",
  fed_funds: "Fed Funds",
};

export function EconomyWidget({ commodities, currencies, yields }: Props) {
  const hasYields = yields && yields.treasury_10y;
  const hasCommodities = commodities.length > 0;
  const hasCurrencies = currencies.length > 0;
  const hasData = hasYields || hasCommodities || hasCurrencies;

  if (!hasData) {
    return (
      <div className="glass-card p-4 sm:p-5">
        <h3 className="text-sm font-semibold text-surface-200 uppercase tracking-wider mb-3">
          Economy
        </h3>
        <div className="flex items-center justify-center py-8 text-surface-500 text-sm">
          <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Loading economy data...
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-surface-200 uppercase tracking-wider mb-3">
        Economy
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Treasury Yields */}
        {hasYields && (
          <div>
            <div className="text-xs text-surface-500 font-medium mb-2 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              Treasury Yields
            </div>
            <div className="space-y-1.5">
              {Object.entries(YIELD_LABELS).map(([key, label]) => {
                const val = yields[key as keyof YieldData];
                if (!val || key === "meta") return null;
                const num = parseFloat(val as string);
                return (
                  <div key={key} className="flex items-center justify-between p-2 rounded-lg bg-surface-800/30">
                    <span className="text-xs font-medium text-surface-300">{label}</span>
                    <span className={`text-xs font-mono tabular-nums font-semibold ${
                      key === "spread_10y_2y"
                        ? num < 0 ? "text-red-400" : "text-green-400"
                        : "text-surface-200"
                    }`}>
                      {num.toFixed(2)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Commodities */}
        <div>
          <div className="text-xs text-surface-500 font-medium mb-2 flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            Commodities
          </div>
          {hasCommodities ? (
            <div className="space-y-1.5">
              {commodities.slice(0, 6).map((q) => (
                <div key={q.symbol} className="flex items-center justify-between p-2 rounded-lg bg-surface-800/30">
                  <span className="text-xs font-medium text-surface-300 truncate">{q.name || q.symbol}</span>
                  <div className="flex flex-col items-end">
                    <span className="text-xs font-mono text-surface-200 tabular-nums">{formatPrice(q.price)}</span>
                    <span className={`text-[10px] tabular-nums ${changeColor(q.change_percent)}`}>
                      {formatPercent(q.change_percent)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-6 text-surface-600 text-xs">
              Waiting for market data...
            </div>
          )}
        </div>

        {/* Currencies */}
        <div>
          <div className="text-xs text-surface-500 font-medium mb-2 flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Currencies
          </div>
          {hasCurrencies ? (
            <div className="space-y-1.5">
              {currencies.slice(0, 6).map((q) => (
                <div key={q.symbol} className="flex items-center justify-between p-2 rounded-lg bg-surface-800/30">
                  <span className="text-xs font-medium text-surface-300 truncate">{q.name || q.symbol}</span>
                  <div className="flex flex-col items-end">
                    <span className="text-xs font-mono text-surface-200 tabular-nums">{formatPrice(q.price)}</span>
                    <span className={`text-[10px] tabular-nums ${changeColor(q.change_percent)}`}>
                      {formatPercent(q.change_percent)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-6 text-surface-600 text-xs">
              Waiting for market data...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
